/**
 * useVidchat.ts — connection, presence, text chat, and media (publish/subscribe).
 *
 * Media model:
 *   - startCam(): getUserMedia → publish_start → Publisher streams encoded
 *     chunks as binary WS frames.
 *   - subscribe(streamId): server sends that stream's chunks; a Subscriber
 *     (bound to a <canvas> via attachCanvas) decodes + paints them.
 *   - Incoming binary frames are routed by streamId to the matching Subscriber.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, type WSClient } from '../lib/ws.js';
import { decodeHeader, TRACK_AUDIO } from '../lib/protocol.js';
import { Publisher } from '../lib/publisher.js';
import { Subscriber } from '../lib/subscriber.js';
import { AudioPublisher, AudioPlayer } from '../lib/audio.js';
import type { ConnState, RoomDir, RosterUser, SessionMe, StreamInfo } from '../lib/types.js';

const DEFAULT_ROOM = 'lobby';

export interface ChatMsg {
    id: number;
    uid: number;
    name: string;
    color: string;
    body: string;
    t: number;
    kind?: 'whisper';     // private DM line (else a normal room message)
    dir?: 'in' | 'out';   // whisper direction
    to?: string;          // whisper recipient name
}

export interface BanRow { uid: number; by: string; reason: string | null; until: number | null; }

export interface ActiveSub {
    streamId: number;
    name: string;
    codec: string;
    width: number;
    height: number;
    muted: boolean;     // local audio mute (your choice only)
    audioOnly: boolean; // mic-only publisher — no video tile, just listen
}

export interface StreamLive {
    state: 'present' | 'away';
    personCount: number;
    pets: { cat: number; dog: number; bird: number };
    onCamSec: number;
    awayEvents: number;
}

const EMPTY_LIVE: StreamLive = { state: 'present', personCount: 0, pets: { cat: 0, dog: 0, bird: 0 }, onCamSec: 0, awayEvents: 0 };

export interface LifetimeStats {
    onCamSec: number;
    sessions: number;
    awayEvents: number;
    maxPersons: number;
    pets: { cat: number; dog: number; bird: number };
    petsTotal: number;
}

export function useVidchat() {
    const wsRef = useRef<WSClient | null>(null);
    const [conn, setConn] = useState<ConnState>('connecting');
    const [me, setMe] = useState<SessionMe | null>(null);
    const [maxSubs, setMaxSubs] = useState(4);
    const [showmeVocab, setShowmeVocab] = useState<string[]>([]);
    const [rooms, setRooms] = useState<RoomDir[]>([]);
    const [currentRoom, setCurrentRoom] = useState(DEFAULT_ROOM);
    const [currentTopic, setCurrentTopic] = useState('');
    const [joinError, setJoinError] = useState<{ room: string; nonce: number } | null>(null);
    const [banList, setBanList] = useState<BanRow[]>([]);
    const [banned, setBanned] = useState<{ by: string; reason: string | null; until: number | null } | null>(null);
    const [disclaimerRequired, setDisclaimerRequired] = useState(true);
    const disclaimerVersionRef = useRef(1);
    const [roster, setRoster] = useState<RosterUser[]>([]);
    const [streams, setStreams] = useState<StreamInfo[]>([]);
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [publishing, setPublishing] = useState(false);
    const [sharingVideo, setSharingVideo] = useState(false);  // false = mic-only publish
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [devices, setDevices] = useState<{ audioIn: MediaDeviceInfo[]; videoIn: MediaDeviceInfo[] }>({ audioIn: [], videoIn: [] });
    const [audioInId, setAudioInId] = useState<string>(() => { try { return localStorage.getItem('vidchat-mic') ?? ''; } catch { return ''; } });
    const [videoInId, setVideoInId] = useState<string>(() => { try { return localStorage.getItem('vidchat-cam') ?? ''; } catch { return ''; } });
    const [activeSubs, setActiveSubs] = useState<ActiveSub[]>([]);
    const [recordingStreams, setRecordingStreams] = useState<Set<number>>(new Set());
    const [notice, setNotice] = useState<string | null>(null);
    const [streamStats, setStreamStats] = useState<Record<number, StreamLive>>({});
    const [userStats, setUserStats] = useState<Record<number, LifetimeStats>>({});
    const [starred, setStarred] = useState<Set<number>>(new Set());
    const localStreamRef = useRef<MediaStream | null>(null);
    const currentRoomRef = useRef(DEFAULT_ROOM);
    const pendingRoomRef = useRef('');

    const publisherRef = useRef<Publisher | null>(null);
    const audioPublisherRef = useRef<AudioPublisher | null>(null);
    const myStreamIdRef = useRef<number | null>(null);
    const subsRef = useRef<Map<number, Subscriber>>(new Map());
    const audioPlayersRef = useRef<Map<number, AudioPlayer>>(new Map());
    const pendingTrackRef = useRef<MediaStreamTrack | null>(null);
    const pendingAudioTrackRef = useRef<MediaStreamTrack | null>(null);
    const msgIdRef = useRef(1);

    useEffect(() => {
        const ws = connect();
        wsRef.current = ws;
        ws.onstate(setConn);

        ws.onbinary((data) => {
            const h = decodeHeader(data);
            if (!h) return;
            if (h.track === TRACK_AUDIO) {
                const player = audioPlayersRef.current.get(h.streamId);
                if (player) player.feed(data);
            } else {
                const sub = subsRef.current.get(h.streamId);
                if (sub) sub.feed(data);
            }
        });

        ws.onmessage((raw) => {
            const f = raw as Record<string, unknown>;
            const evt = f.evt as string | undefined;
            const res = f.res as string | undefined;

            if (evt === 'session') {
                setMe(f.me as SessionMe);
                if (typeof f.max_subs === 'number') setMaxSubs(f.max_subs);
                if (Array.isArray(f.showme_vocab)) setShowmeVocab(f.showme_vocab as string[]);
                if (typeof f.disclaimer_version === 'number') disclaimerVersionRef.current = f.disclaimer_version;
                const needDisclaimer = f.disclaimer_required !== false;
                setDisclaimerRequired(needDisclaimer);
                // only enter the chat once the disclaimer is satisfied
                if (!needDisclaimer) { ws.send({ cmd: 'join_room', room: DEFAULT_ROOM }); ws.send({ cmd: 'list_rooms' }); }
                return;
            }
            if (res === 'accept_disclaimer') {
                if (f.ok) { setDisclaimerRequired(false); ws.send({ cmd: 'join_room', room: DEFAULT_ROOM }); ws.send({ cmd: 'list_rooms' }); }
                return;
            }
            if (typeof f.err === 'string') {
                const code = f.err;
                const room = String(f.room ?? '');
                if (code === 'bad_password') { pendingRoomRef.current = ''; setJoinError({ room, nonce: Date.now() }); }
                else if (code === 'no_such_room') { pendingRoomRef.current = ''; setNotice(`That room ("${room}") is no longer available.`); ws.send({ cmd: 'list_rooms' }); }
                else if (code === 'create_failed') {
                    pendingRoomRef.current = '';
                    const d = String(f.detail ?? '');
                    setNotice(d === 'room_exists' ? 'A room with that name already exists.'
                        : d === 'name_reserved' ? 'That room name is reserved.'
                        : 'Too many rooms right now — try again shortly.');
                } else if (code === 'auth_required') { setNotice('You need to be logged in to do that.'); }
                else if (code === 'forbidden') { setNotice(`Not allowed${f.detail ? ` — ${String(f.detail)}` : ''}.`); }
                else if (code === 'user_offline') { setNotice('That user is not online right now.'); }
                return;
            }
            if (evt === 'room_list') {
                if (Array.isArray(f.rooms)) setRooms(f.rooms as RoomDir[]);
                return;
            }
            if (evt === 'topic') {
                setCurrentTopic(String(f.topic ?? ''));
                return;
            }
            if (evt === 'whisper') {
                setMessages((prev) => {
                    const next = [...prev, {
                        id: msgIdRef.current++,
                        uid: Number(f.fromUid ?? 0), name: String(f.from ?? '?'),
                        color: String(f.color ?? '#9b77cc'), body: String(f.body ?? ''), t: Number(f.t),
                        kind: 'whisper' as const, dir: (f.dir === 'out' ? 'out' : 'in') as 'in' | 'out', to: String(f.to ?? ''),
                    }];
                    return next.length > 200 ? next.slice(-200) : next;
                });
                return;
            }
            if (evt === 'ban_list') {
                if (Array.isArray(f.bans)) setBanList(f.bans as BanRow[]);
                return;
            }
            if (evt === 'banned') {
                setBanned({ by: String(f.by ?? 'a moderator'), reason: (f.reason as string | null) ?? null, until: (f.until as number | null) ?? null });
                return;
            }
            if (evt === 'kicked') {
                setNotice(`You were kicked by ${String(f.by ?? 'a moderator')}${f.reason ? ` — ${String(f.reason)}` : ''}.`);
                return;
            }
            if (evt === 'room_state' || evt === 'roster') {
                const rm = String(f.room ?? '');
                // a room-switch we initiated just landed: tear down the old room, swap in the new
                if (evt === 'room_state' && rm && rm === pendingRoomRef.current && rm !== currentRoomRef.current) {
                    const old = currentRoomRef.current;
                    publisherRef.current?.stop(); publisherRef.current = null;
                    audioPublisherRef.current?.stop(); audioPublisherRef.current = null;
                    myStreamIdRef.current = null;
                    pendingTrackRef.current = null; pendingAudioTrackRef.current = null;
                    localStreamRef.current?.getTracks().forEach((t) => t.stop());
                    localStreamRef.current = null;
                    setLocalStream(null); setPublishing(false); setSharingVideo(false);
                    for (const s of subsRef.current.values()) s.stop();
                    subsRef.current.clear();
                    for (const p of audioPlayersRef.current.values()) p.stop();
                    audioPlayersRef.current.clear();
                    setActiveSubs([]); setStreamStats({}); setUserStats({}); setStarred(new Set()); setMessages([]); setCurrentTopic('');
                    if (old && old !== rm) ws.send({ cmd: 'leave_room', room: old });
                    currentRoomRef.current = rm;
                    setCurrentRoom(rm);
                    pendingRoomRef.current = '';
                    setJoinError(null);
                }
                if (typeof f.topic === 'string') setCurrentTopic(f.topic);
                if (Array.isArray(f.users)) setRoster(f.users as RosterUser[]);
                if (Array.isArray(f.streams)) setStreams(f.streams as StreamInfo[]);
                return;
            }
            if (evt === 'chat_history') {
                const rows = Array.isArray(f.messages) ? f.messages as Array<Record<string, unknown>> : [];
                setMessages(rows.map((m) => ({
                    id: msgIdRef.current++,
                    uid: Number(m.uid), name: String(m.name),
                    color: String(m.color ?? '#9b77cc'), body: String(m.body), t: Number(m.t),
                })));
                return;
            }
            if (evt === 'say') {
                setMessages((prev) => {
                    const next = [...prev, {
                        id: msgIdRef.current++,
                        uid: Number(f.uid), name: String(f.name),
                        color: String(f.color ?? '#9b77cc'), body: String(f.body), t: Number(f.t),
                    }];
                    return next.length > 200 ? next.slice(-200) : next;
                });
                return;
            }
            if (evt === 'stream_ended') {
                const sid = Number(f.stream_id);
                teardownSub(sid);
                setStreamStats((prev) => { const n = { ...prev }; delete n[sid]; return n; });
                setUserStats((prev) => { const n = { ...prev }; delete n[sid]; return n; });
                return;
            }
            if (evt === 'analytics') {
                const sid = Number(f.stream_id);
                const pets = (f.pets as StreamLive['pets']) ?? EMPTY_LIVE.pets;
                setStreamStats((prev) => ({
                    ...prev,
                    [sid]: {
                        state: prev[sid]?.state ?? 'present',
                        personCount: Number(f.person_count ?? 0),
                        pets,
                        onCamSec: Number(f.on_cam_sec ?? 0),
                        awayEvents: Number(f.away_events ?? 0),
                    },
                }));
                return;
            }
            if (evt === 'presence_state') {
                const sid = Number(f.stream_id);
                const state = (f.state === 'away' ? 'away' : 'present') as StreamLive['state'];
                setStreamStats((prev) => ({ ...prev, [sid]: { ...(prev[sid] ?? EMPTY_LIVE), state } }));
                return;
            }
            if (evt === 'star') {
                const sid = Number(f.stream_id);
                setStarred((prev) => { const n = new Set(prev); n.add(sid); return n; });
                setTimeout(() => setStarred((prev) => { const n = new Set(prev); n.delete(sid); return n; }), 6000);
                return;
            }
            if (evt === 'user_stats') {
                const sid = Number(f.stream_id);
                const pets = (f.pets as LifetimeStats['pets']) ?? { cat: 0, dog: 0, bird: 0 };
                setUserStats((prev) => ({
                    ...prev,
                    [sid]: {
                        onCamSec: Number(f.on_cam_sec ?? 0), sessions: Number(f.sessions ?? 0),
                        awayEvents: Number(f.away_events ?? 0), maxPersons: Number(f.max_persons ?? 0),
                        pets, petsTotal: Number(f.pets_total ?? 0),
                    },
                }));
                return;
            }
            if (evt === 'request_keyframe') {
                // server (on behalf of a new viewer) wants our encoder to emit a key
                publisherRef.current?.requestKeyframe();
                return;
            }
            if (evt === 'auto_stopped') {
                // server's presence gate stopped our cam (no person detected)
                publisherRef.current?.stop(); publisherRef.current = null;
                audioPublisherRef.current?.stop(); audioPublisherRef.current = null;
                myStreamIdRef.current = null;
                pendingTrackRef.current = null;
                pendingAudioTrackRef.current = null;
                localStreamRef.current?.getTracks().forEach((t) => t.stop());
                localStreamRef.current = null;
                setLocalStream(null);
                setPublishing(false);
                setSharingVideo(false);
                setNotice('Your camera was turned off — no one was detected on screen for 30 seconds.');
                return;
            }
            if (res === 'publish_start') {
                const sid = Number(f.stream_id);
                myStreamIdRef.current = sid;
                const track = pendingTrackRef.current;
                if (track) {
                    const pub = new Publisher({ streamId: sid, track, sendBinary: (d) => ws.sendBinary(d) });
                    publisherRef.current = pub;
                    void pub.start();
                    pendingTrackRef.current = null;
                }
                const aTrack = pendingAudioTrackRef.current;
                if (aTrack) {
                    const apub = new AudioPublisher(sid, aTrack, (d) => ws.sendBinary(d));
                    audioPublisherRef.current = apub;
                    void apub.start();
                    pendingAudioTrackRef.current = null;
                }
                setPublishing(true);   // true for both cam and mic-only
                return;
            }
            if (res === 'publish_stop') {
                setPublishing(false);
                return;
            }
            if (res === 'record_start') {
                const sid = Number(f.stream_id);
                setRecordingStreams((prev) => new Set(prev).add(sid));
                return;
            }
            if (res === 'record_stop') {
                const sid = Number(f.stream_id);
                setRecordingStreams((prev) => { const n = new Set(prev); n.delete(sid); return n; });
                return;
            }
        });

        return () => {
            ws.close();
            wsRef.current = null;
            publisherRef.current?.stop();
            audioPublisherRef.current?.stop();
            for (const s of subsRef.current.values()) s.stop();
            for (const p of audioPlayersRef.current.values()) p.stop();
            subsRef.current.clear();
            audioPlayersRef.current.clear();
        };
    }, []);

    const send = useCallback((frame: object) => { wsRef.current?.send(frame); }, []);

    const sendChat = useCallback((body: string) => {
        const trimmed = body.trim();
        if (!trimmed) return;
        // "@name message" → private whisper (target name is the first token)
        const m = trimmed.match(/^@([^\s]+)\s+([\s\S]+)$/);
        if (m) { wsRef.current?.send({ cmd: 'whisper', to: m[1], body: m[2] }); return; }
        wsRef.current?.send({ cmd: 'say', room: currentRoomRef.current, body: trimmed });
    }, []);

    const setRoomTopic = useCallback((topic: string) => {
        wsRef.current?.send({ cmd: 'set_topic', room: currentRoomRef.current, topic });
    }, []);

    const modKick = useCallback((uid: number, reason?: string) => {
        wsRef.current?.send({ cmd: 'mod_kick', uid, ...(reason ? { reason } : {}) });
    }, []);
    const modBan = useCallback((uid: number, minutes?: number, reason?: string) => {
        wsRef.current?.send({ cmd: 'mod_ban', uid, ...(minutes ? { minutes } : {}), ...(reason ? { reason } : {}) });
    }, []);
    const modUnban = useCallback((uid: number) => { wsRef.current?.send({ cmd: 'mod_unban', uid }); }, []);
    const modListBans = useCallback(() => { wsRef.current?.send({ cmd: 'mod_listbans' }); }, []);

    /** Join an existing room (open or, with password, a locked one). */
    const joinRoom = useCallback((name: string, password?: string) => {
        if (name === currentRoomRef.current) return;
        setJoinError(null);
        pendingRoomRef.current = name;
        wsRef.current?.send({ cmd: 'join_room', room: name, ...(password ? { password } : {}) });
    }, []);

    /** Create a new party room (optionally password-gated); auto-joins on success. */
    const createRoom = useCallback((name: string, password?: string) => {
        setJoinError(null);
        pendingRoomRef.current = name;
        wsRef.current?.send({ cmd: 'create_room', room: name, ...(password ? { password } : {}) });
    }, []);

    const refreshRooms = useCallback(() => { wsRef.current?.send({ cmd: 'list_rooms' }); }, []);

    const acceptDisclaimer = useCallback(() => {
        wsRef.current?.send({ cmd: 'accept_disclaimer', version: disclaimerVersionRef.current });
    }, []);

    const beginPublish = useCallback(async (withVideo: boolean) => {
        if (publishing || pendingTrackRef.current || pendingAudioTrackRef.current) return;
        const audio: MediaTrackConstraints = {
            echoCancellation: true, noiseSuppression: true, sampleRate: 48000, channelCount: 1,
            ...(audioInId ? { deviceId: { ideal: audioInId } } : {}),
        };
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia(
                withVideo
                    ? { video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 }, ...(videoInId ? { deviceId: { ideal: videoInId } } : {}) }, audio }
                    : { audio },
            );
        } catch {
            setNotice(withVideo ? 'Could not access your camera/microphone.' : 'Could not access your microphone.');
            return;
        }
        const track = withVideo ? (stream.getVideoTracks()[0] ?? null) : null;
        const aTrack = stream.getAudioTracks()[0] ?? null;
        if (!track && !aTrack) { stream.getTracks().forEach((t) => t.stop()); setNotice('No camera or microphone available.'); return; }
        const s = track?.getSettings();
        setLocalStream(stream);
        localStreamRef.current = stream;
        setSharingVideo(!!track);
        setNotice(null);
        pendingTrackRef.current = track;
        pendingAudioTrackRef.current = aTrack;
        wsRef.current?.send({
            cmd: 'publish_start', room: currentRoomRef.current,
            codec: 'vp8', width: s?.width ?? 640, height: s?.height ?? 480,
            has_audio: !!aTrack, has_video: !!track,
        });
    }, [publishing, audioInId, videoInId]);

    const startCam = useCallback(() => { void beginPublish(true); }, [beginPublish]);
    const startMic = useCallback(() => { void beginPublish(false); }, [beginPublish]);

    const listDevices = useCallback(async () => {
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            setDevices({
                audioIn: all.filter((d) => d.kind === 'audioinput'),
                videoIn: all.filter((d) => d.kind === 'videoinput'),
            });
        } catch { /* ignore */ }
    }, []);

    const chooseAudioIn = useCallback((id: string) => { setAudioInId(id); try { localStorage.setItem('vidchat-mic', id); } catch { /* ignore */ } }, []);
    const chooseVideoIn = useCallback((id: string) => { setVideoInId(id); try { localStorage.setItem('vidchat-cam', id); } catch { /* ignore */ } }, []);

    const stopCam = useCallback(() => {
        publisherRef.current?.stop();
        publisherRef.current = null;
        audioPublisherRef.current?.stop();
        audioPublisherRef.current = null;
        myStreamIdRef.current = null;
        pendingTrackRef.current = null;
        pendingAudioTrackRef.current = null;
        localStream?.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
        setLocalStream(null);
        setPublishing(false);
        setSharingVideo(false);
        wsRef.current?.send({ cmd: 'publish_stop' });
    }, [localStream]);

    const subscribe = useCallback((info: StreamInfo) => {
        if (subsRef.current.has(info.stream_id)) return;
        // at capacity → evict the OLDEST watched stream and replace it (don't refuse)
        const atCap = activeSubs.length >= maxSubs;
        const evictId = atCap ? activeSubs[0]?.streamId : undefined;
        if (evictId !== undefined) {
            wsRef.current?.send({ cmd: 'unsubscribe', stream_id: evictId });
            subsRef.current.get(evictId)?.stop(); subsRef.current.delete(evictId);
            audioPlayersRef.current.get(evictId)?.stop(); audioPlayersRef.current.delete(evictId);
        }
        wsRef.current?.send({ cmd: 'subscribe', stream_id: info.stream_id });
        // audio player starts now (inside the click gesture so AudioContext may resume);
        // the video subscriber attaches when its canvas mounts.
        if (info.meta?.hasAudio && !audioPlayersRef.current.has(info.stream_id)) {
            const player = new AudioPlayer();
            player.start();
            audioPlayersRef.current.set(info.stream_id, player);
        }
        setActiveSubs((prev) => {
            const base = evictId !== undefined ? prev.filter((a) => a.streamId !== evictId) : prev;
            if (base.some((a) => a.streamId === info.stream_id)) return base;
            return [...base, {
                streamId: info.stream_id, name: info.name,
                codec: info.meta?.codec ?? 'vp8', width: info.meta?.width ?? 640, height: info.meta?.height ?? 480,
                muted: false, audioOnly: info.meta?.hasVideo === false,
            }];
        });
    }, [activeSubs, maxSubs]);

    const toggleMute = useCallback((streamId: number) => {
        setActiveSubs((prev) => prev.map((a) => {
            if (a.streamId !== streamId) return a;
            const muted = !a.muted;
            audioPlayersRef.current.get(streamId)?.setMuted(muted);
            return { ...a, muted };
        }));
    }, []);

    const teardownSub = useCallback((streamId: number) => {
        const sub = subsRef.current.get(streamId);
        if (sub) { sub.stop(); subsRef.current.delete(streamId); }
        const player = audioPlayersRef.current.get(streamId);
        if (player) { player.stop(); audioPlayersRef.current.delete(streamId); }
        setActiveSubs((prev) => prev.filter((a) => a.streamId !== streamId));
        setStreamStats((prev) => { const n = { ...prev }; delete n[streamId]; return n; });
    }, []);

    const unsubscribe = useCallback((streamId: number) => {
        wsRef.current?.send({ cmd: 'unsubscribe', stream_id: streamId });
        teardownSub(streamId);
    }, [teardownSub]);

    const toggleRecord = useCallback((streamId: number, on: boolean) => {
        wsRef.current?.send(on
            ? { cmd: 'record_start', stream_id: streamId }
            : { cmd: 'record_stop', stream_id: streamId, remux: true });
    }, []);

    /** Bind a mounted <canvas> to a Subscriber for the given stream. */
    const attachCanvas = useCallback((sub: ActiveSub, canvas: HTMLCanvasElement) => {
        if (subsRef.current.has(sub.streamId)) return;
        const s = new Subscriber({
            streamId: sub.streamId, codec: sub.codec, width: sub.width, height: sub.height, canvas,
            onNeedKeyframe: (sid) => wsRef.current?.send({ cmd: 'request_keyframe', stream_id: sid }),
        });
        s.start();
        subsRef.current.set(sub.streamId, s);
        // proactively ask for a keyframe so we don't wait a full interval
        wsRef.current?.send({ cmd: 'request_keyframe', stream_id: sub.streamId });
    }, []);

    return {
        conn, me, maxSubs, showmeVocab, rooms, currentRoom, currentTopic, joinError, banList, banned, disclaimerRequired, roster, streams, messages, publishing, sharingVideo, localStream, activeSubs, recordingStreams, notice, streamStats, userStats, starred,
        devices, audioInId, videoInId, listDevices, chooseAudioIn, chooseVideoIn, acceptDisclaimer,
        send, sendChat, joinRoom, createRoom, refreshRooms, setRoomTopic, whisper: (to: string, body: string) => wsRef.current?.send({ cmd: 'whisper', to, body }),
        modKick, modBan, modUnban, modListBans,
        startCam, startMic, stopCam, subscribe, unsubscribe, toggleMute, attachCanvas, toggleRecord, dismissNotice: () => setNotice(null),
    } as const;
}
